/**
 * A sibling application's 424, read as a fact rather than as a sentence.
 *
 * The backend proxy answers 424 when an application that owns a person's data
 * (Data Studio's tables, Automation Studio's API registry) would not let
 * analytics act for them. For a long time that carried one sentence for every
 * cause, and the sentence was written for the most common cause — a missing
 * cross-application grant, which only an administrator can clear.
 *
 * It is not the most common cause in front of a person. This is:
 *
 *   Identity Studio sets the shared suite cookie on the connection it answers,
 *   and for analytics' own sign-in form that connection is the backend's, not
 *   the browser's. So a password sign-in here signs somebody into Analytics
 *   ALONE — there is no Data Studio session for the same person to act through,
 *   and delegation is refused with "The subject session is not part of a live
 *   shared sign-in." The identical refusal comes back when the browser's suite
 *   session belongs to a *different account*.
 *
 * Both are fixed by one act the person can perform in seconds: sign in through
 * the suite, as the account that owns the data. So the proxy now sends a
 * `remedy`, and this reads it. Branching on `remedy` rather than on `detail` is
 * the point — the sentence is for a human, and parsing English to decide what a
 * button does is how the wrong button gets shown.
 */

import { ApiError } from './api';

/** What the person should DO. Mirrors `vaarta_proxy.REMEDY_*` exactly. */
export type Remedy =
  /** Sign in across the suite: Analytics and the target are not the same account here. */
  | 'sign_in_to_suite'
  /** This account may not use the target at all. Signing in again cannot change it. */
  | 'wrong_account'
  /** A cross-application grant is missing. Only an administrator clears it. */
  | 'ask_admin'
  /** Delegation worked and the far side refused anyway. Often temporary. */
  | 'retry';

const REMEDIES: readonly Remedy[] = ['sign_in_to_suite', 'wrong_account', 'ask_admin', 'retry'];

export interface SiblingRefusal {
  /** The Identity Studio application that refused, e.g. `data-studio`. */
  target: string;
  /** What the far side answered — 401 or 403. */
  upstreamStatus: number;
  /** The backend's sentence. Already written for a person; do not rewrite it. */
  detail: string;
  remedy: Remedy;
  /** Identity Studio's reason, for logs and for tests. Not shown as-is. */
  delegationReason: string;
  /** Which account Analytics is signed in as. `''` when the backend had none. */
  account: string;
  /** Whether analytics held a token minted for the target when it was refused. */
  delegated: boolean;
}

/**
 * The refusal behind an error, or `null` if it is not one.
 *
 * Tolerant on purpose: an older backend answers 424 with only `detail`, and a
 * build that predates `remedy` must still produce a usable refusal rather than
 * nothing. Such a response reads as `ask_admin`, which is the sentence that
 * backend was already sending.
 */
export function siblingRefusalOf(error: unknown): SiblingRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 424) return null;
  const body = (error.payload ?? null) as Record<string, unknown> | null;
  /*
   * ⚠️ TWO SHAPES, one condition — and reading only the first is how half of
   * these would silently become `ask_admin`.
   *
   * The browser proxy answers a FLAT body (`{detail, target, remedy, …}`); the
   * chart-data route raises a FastAPI `HTTPException` whose `detail` is an
   * OBJECT (`{message, target, remedy, …}`), because that is what FastAPI does
   * with a dict detail. A table that will not save and a tile that will not
   * load are the same refusal, so both are read here.
   */
  const nested =
    body?.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)
      ? (body.detail as Record<string, unknown>)
      : null;
  const read = (key: string): string => {
    const flat = body?.[key];
    if (typeof flat === 'string' && flat) return flat;
    const inner = nested?.[key];
    return typeof inner === 'string' ? inner : '';
  };
  const num = (key: string): number => {
    const flat = body?.[key];
    if (typeof flat === 'number') return flat;
    return typeof nested?.[key] === 'number' ? (nested[key] as number) : 0;
  };
  const remedy = read('remedy') as Remedy;
  return {
    target: read('target'),
    upstreamStatus: num('upstream_status'),
    // `message` is the nested shape's name for the sentence. `error.message` is
    // the last resort and is already extracted from either by the transport.
    detail: read('detail') || read('message') || error.message,
    remedy: REMEDIES.includes(remedy) ? remedy : 'ask_admin',
    delegationReason: read('delegation_reason'),
    account: read('account'),
    delegated: body?.delegated === true || nested?.delegated === true,
  };
}

/**
 * Is this the two-accounts-in-one-browser case?
 *
 * The only one where offering a sign-in is honest. `ask_admin` and `retry` are
 * not — a person who signs in again against a missing grant ends up exactly
 * where they started, having been told the fix was theirs.
 */
export function isAccountMismatch(refusal: SiblingRefusal | null): boolean {
  return refusal?.remedy === 'sign_in_to_suite' || refusal?.remedy === 'wrong_account';
}
